/**
 * TEN MINUTES OF NOTHING, AND THE PORTAL LETS GO.
 *
 *   "every Orchid Portal login should have a timed non-activity auto-logout,
 *    so that if a staff member has not been using the system for 10 minutes,
 *    they are automatically logged out."
 *
 * The portal is where FICA documents, identity records, disputes and staff
 * salaries are read. An unattended laptop in an open-plan office is the most
 * ordinary way any of that leaks, and it leaks under a real staff identity —
 * so the audit log will faithfully record the wrong person's name against the
 * disclosure. This is the countermeasure, and it is why the clock is short.
 *
 * WHAT COUNTS AS ACTIVITY
 *
 * Pointer, keyboard, touch, scroll and wheel. Deliberately NOT mouse movement:
 * a nudged desk or a sleeping cat resets a mousemove listener, and a timer that
 * a stationary room can keep alive is not a timer.
 *
 * Returning to the tab counts. Somebody who left for eleven minutes is signed
 * out the moment they look, rather than at some point after — visibilitychange
 * is checked against elapsed time rather than treated as activity, so coming
 * back does not grant a fresh ten minutes.
 *
 * WHY THE LAST MINUTE IS A WARNING
 *
 * The ten minutes are unchanged. The warning occupies the last sixty seconds of
 * them, and exists because the screens most worth protecting are also the ones
 * with the most typing in them: a captured reason is a paragraph somebody
 * composed, and losing it to an invisible clock teaches staff to keep the
 * portal open in a second window, which defeats the whole control.
 *
 * THE TIMER IS NOT A SECURITY BOUNDARY, AND CANNOT BE
 *
 * Anyone editing this bundle can disable it. What actually expires is the
 * Supabase session and the RLS behind it. This closes the far more common
 * non-adversarial case — a walked-away-from desk — and nothing here should be
 * read as a claim to close more than that.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { O, SANS } from './ui';

const ACTIVITY = ['mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'pointerdown'];

export default function useIdleSignOut({
  enabled = false,
  minutes = 10,
  warnSeconds = 60,
  onIdle = () => {},
}) {
  const [secondsLeft, setSecondsLeft] = useState(null);

  const lastActive = useRef(Date.now());
  // Held in a ref so the interval below never needs onIdle in its dependency
  // list. A parent that recreates its sign-out function on every render would
  // otherwise tear down and rebuild the timer continuously, which is the
  // classic way an idle timer quietly never fires.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const fired = useRef(false);

  const stay = useCallback(() => {
    lastActive.current = Date.now();
    setSecondsLeft(null);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setSecondsLeft(null);
      return undefined;
    }

    fired.current = false;
    lastActive.current = Date.now();

    const limit = minutes * 60 * 1000;
    const warnAt = limit - (warnSeconds * 1000);

    const touch = () => {
      lastActive.current = Date.now();
      setSecondsLeft((current) => (current === null ? null : null));
    };

    ACTIVITY.forEach((type) => {
      window.addEventListener(type, touch, { passive: true });
    });

    const tick = setInterval(() => {
      const idleFor = Date.now() - lastActive.current;

      if (idleFor >= limit) {
        // Guarded: signing out is asynchronous, and a second call while the
        // first is in flight produces two sign-outs and a confusing flash.
        if (!fired.current) {
          fired.current = true;
          setSecondsLeft(null);
          onIdleRef.current();
        }
        return;
      }

      setSecondsLeft(idleFor >= warnAt
        ? Math.max(1, Math.ceil((limit - idleFor) / 1000))
        : null);
    }, 1000);

    return () => {
      clearInterval(tick);
      ACTIVITY.forEach((type) => window.removeEventListener(type, touch));
    };
  }, [enabled, minutes, warnSeconds]);

  return { secondsLeft, stay };
}

/**
 * The last sixty seconds, said out loud.
 *
 * role="alertdialog" rather than a quiet banner: this is a deadline, it is
 * about to take an action the person did not ask for, and a screen reader user
 * needs it announced rather than discovered.
 */
export const IdleWarning = ({ secondsLeft, onStay }) => {
  if (!secondsLeft) return null;
  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-label="You are about to be signed out"
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 80,
        background: O.green, color: '#FFFFFF', fontFamily: SANS,
        padding: '14px 20px calc(14px + env(safe-area-inset-bottom, 0px))',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        justifyContent: 'center', gap: 14,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600 }}>
        Signing you out in {secondsLeft} second{secondsLeft === 1 ? '' : 's'} — the portal
        has been idle.
      </span>
      <button
        onClick={onStay}
        style={{
          padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
          background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.4)',
          color: '#FFFFFF', font: 'inherit', fontSize: 12.5, fontWeight: 700,
        }}
      >
        Keep me signed in
      </button>
    </div>
  );
};
